import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ownerApi } from '../../api/portal';
import usePortalQuery from '../../hooks/usePortalQuery';
import { currentMonth, lineTotals, money, validRange } from '../../pages/ownerPortal/finance';
import { QueryStatus, StatementLines, Totals } from './PortalUI';

export function useVehicleStatements(vehicleId, { from, to, filters = {}, enabled = true }) {
  return usePortalQuery(['vehicle-statements-range', vehicleId, from, to, filters],
    (signal) => ownerApi.detail(vehicleId, { from, to, ...filters }, signal), enabled);
}

export function useMonthRange() {
  const [params, setParams] = useSearchParams();
  const month = currentMonth();
  const from = params.get('from') || month;
  const to = params.get('to') || month;
  const change = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  return { from, to, valid: validRange(from, to), change };
}

const monthlyRows = (statements) => {
  const months = new Map();
  statements.forEach((statement) => {
    const period = statement.period || 'Unknown';
    months.set(period, [...(months.get(period) || []), statement]);
  });
  return [...months.entries()].sort(([a], [b]) => a.localeCompare(b))
    .map(([period, items]) => ({ period, ...lineTotals(items) }));
};

export default function VehicleEarnings({ vehicleId, filters, enabled = true }) {
  const range = useMonthRange();
  const query = useVehicleStatements(vehicleId, { from: range.from, to: range.to, filters, enabled: enabled && range.valid });
  const detail = query.data;
  const statements = detail?.statements || [];
  const currency = statements[0]?.currency || 'USD';
  const totals = lineTotals(statements);
  const rows = monthlyRows(statements);
  return <section className="portal-card" aria-label="Earnings and costs">
    <h2>Earnings & costs</h2>
    <div className="portal-controls">
      <label>From month<input type="month" value={range.from} onChange={(event) => range.change('from', event.target.value)} /></label>
      <label>To month<input type="month" value={range.to} onChange={(event) => range.change('to', event.target.value)} /></label>
    </div>
    {!range.valid && <p role="alert" className="portal-error">Choose both months, with the end not before the start, spanning at most 12 months.</p>}
    {range.valid && <QueryStatus query={query} />}
    {range.valid && detail && (statements.length ? <>
      <Totals value={totals} currency={currency} />
      {rows.length > 1 && <div className="portal-table-scroll"><table><caption>Monthly breakdown ({currency})</caption>
        <thead><tr><th>Month</th><th>Earnings</th><th>Costs</th><th>Net income</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.period}><td>{row.period}</td><td>{money(row.total_earnings, currency)}</td>
          <td>{money(row.total_cost, currency)}</td><td>{money(row.net, currency)}</td></tr>)}</tbody></table></div>}
      <div className="portal-lines-grid">
        <StatementLines kind="earning" items={statements.flatMap((statement) => statement.earnings)} currency={currency} />
        <StatementLines kind="cost" items={statements.flatMap((statement) => statement.costs)} currency={currency} />
      </div>
    </> : <p>No earnings or costs recorded for {range.from === range.to ? range.from : `${range.from} to ${range.to}`}.</p>)}
  </section>;
}
